import { useState, useMemo, useRef, useCallback, useEffect } from "react";

type Props = {
  label?: string;
  placeholder?: string;
  onSubmit: (address: Address | string) => void | Promise<void>;
  buttonText?: string;
  helperText?: string;
};

export interface Address {
  line1: string;
  line2?: string;
  city: string;
  postcode: string;
  uprn?: string;
}

// Get API base URL from environment. Default to empty so calls go same-origin
// (CloudFront proxies /api/* to API Gateway).
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '';

// Session caches (in-memory, cleared on page refresh)
const validationCache = new Map<string, { postcode: string; latitude?: number; longitude?: number }>();
const addressCache = new Map<string, Address[]>();

/**
 * Detects if input looks like a UK postcode
 * Matches: SW1A 1AA, M1 1AE, CR2 6XH, etc.
 */
function isLikelyUkPostcode(value: string): boolean {
  const v = value.trim().toUpperCase().replace(/\s/g, '');
  return /^[A-Z]{1,2}\d{1,2}[A-Z]?\d[A-Z]{2}$/.test(v);
}

function normalizePostcode(value: string): string {
  return value.replace(/\s/g, '').toUpperCase();
}

export default function AddressInput({
  label = "Property address or postcode",
  placeholder = "e.g. SW1A 1AA",
  onSubmit,
  buttonText = "Continue",
  helperText = "Enter a UK postcode to find addresses, or type the full address.",
}: Props) {
  const [inputValue, setInputValue] = useState("");
  const [addressOptions, setAddressOptions] = useState<Address[]>([]);
  const [selectedAddress, setSelectedAddress] = useState<Address | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [postcodeConfirmed, setPostcodeConfirmed] = useState<string | null>(null);

  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const addressInputRef = useRef<HTMLInputElement>(null);
  const trimmed = useMemo(() => inputValue.trim(), [inputValue]);

  /**
   * Phase 1 (FREE): Validate postcode only via Postcodes.io.
   * No Ideal Postcodes credit is used.
   */
  const validatePostcode = useCallback(async (postcode: string) => {
    const normalized = normalizePostcode(postcode);

    // Check validation cache
    if (validationCache.has(normalized)) {
      setPostcodeConfirmed(validationCache.get(normalized)!.postcode);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await fetch(`${API_BASE_URL}/api/address/lookup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ postcode, validateOnly: true }),
      });

      if (response.ok) {
        const data = await response.json();
        validationCache.set(normalized, {
          postcode: data.postcode || postcode,
          latitude: data.latitude,
          longitude: data.longitude,
        });
        setPostcodeConfirmed(data.postcode || postcode);
        setAddressOptions([]);
      } else if (response.status === 404) {
        setError("Postcode not found. Check and try again, or enter the full address.");
        setPostcodeConfirmed(null);
      } else if (response.status === 400) {
        setError("Enter a valid UK postcode (e.g. SW1A 1AA).");
        setPostcodeConfirmed(null);
      } else {
        setError("Address lookup temporarily unavailable. Enter the full address to continue.");
        setPostcodeConfirmed(null);
      }
    } catch {
      setError("Address lookup temporarily unavailable. Enter the full address to continue.");
      setPostcodeConfirmed(null);
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Phase 2 (PAID): Fetch full address list from Ideal Postcodes.
   * Only called when user explicitly clicks "Find addresses".
   */
  const fetchAddresses = useCallback(async () => {
    if (!postcodeConfirmed) return;

    const normalized = normalizePostcode(postcodeConfirmed);

    // Check address cache
    if (addressCache.has(normalized)) {
      setAddressOptions(addressCache.get(normalized)!);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await fetch(`${API_BASE_URL}/api/address/lookup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ postcode: postcodeConfirmed }),
      });

      if (response.ok) {
        const data = await response.json();
        const addresses: Address[] = data.addresses || [];
        addressCache.set(normalized, addresses);

        if (addresses.length > 0) {
          setAddressOptions(addresses);
        } else {
          setError("No addresses found for this postcode. Enter the full address above.");
        }
      } else {
        setError("Address lookup temporarily unavailable. Enter the full address to continue.");
      }
    } catch {
      setError("Address lookup temporarily unavailable. Enter the full address to continue.");
    } finally {
      setLoading(false);
    }
  }, [postcodeConfirmed]);

  /**
   * Debounced postcode detection — Phase 1 only (FREE validation).
   */
  useEffect(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    if (inputValue.length < 5) {
      setAddressOptions([]);
      setPostcodeConfirmed(null);
      return;
    }

    if (isLikelyUkPostcode(inputValue)) {
      debounceTimerRef.current = setTimeout(() => {
        void validatePostcode(inputValue);
      }, 400);
    } else {
      setAddressOptions([]);
      setPostcodeConfirmed(null);
    }

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [inputValue, validatePostcode]);

  const handleSelectAddress = (address: Address) => {
    const fullAddress = [address.line1, address.line2, address.city, address.postcode]
      .filter(Boolean)
      .join(', ');

    setInputValue(fullAddress);
    setSelectedAddress(address);
    setAddressOptions([]);
    setPostcodeConfirmed(null);
    setError(null);
  };

  const validate = () => {
    if (!trimmed) return "Enter a property address or postcode.";
    if (trimmed.length < 5) return "Enter a valid UK postcode or full property address.";
    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const err = validate();
    setError(err);
    if (err) {
      addressInputRef.current?.focus();
      return;
    }

    try {
      await onSubmit(selectedAddress || trimmed);
    } catch (submitError) {
      setError(submitError instanceof Error ? submitError.message : 'An error occurred');
      addressInputRef.current?.focus();
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-3" aria-label="Address lookup">
      <div>
        <label className="label" htmlFor="address">
          {label}
        </label>
        <p className="helper">{helperText}</p>
      </div>

      <div className="flex flex-col gap-3 sm:flex-row sm:items-start">
        <div className="flex-1">
          <input
            ref={addressInputRef}
            id="address"
            name="address"
            type="text"
            inputMode="text"
            autoComplete="postal-code street-address"
            className={`input ${error ? 'border-red-500 border-l-4 border-l-[#dc2626]' : ''}`}
            placeholder={placeholder}
            value={inputValue}
            onChange={(e) => {
              setInputValue(e.target.value);
              setSelectedAddress(null);
              if (error) setError(null);
            }}
            aria-invalid={!!error}
            aria-describedby={error ? "address-error" : undefined}
          />

          {/* Loading indicator */}
          {loading && (
            <p className="mt-2 text-xs text-slate-600">Looking up addresses...</p>
          )}

          {/* Error message */}
          {error && (
            <div id="address-error" className="error" role="alert">
              {error}
            </div>
          )}

          {/* Postcode confirmed — show "Find addresses" button (Phase 2 trigger) */}
          {postcodeConfirmed && !loading && !error && addressOptions.length === 0 && (
            <div className="mt-2 p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
              <p className="text-sm text-emerald-800">
                ✓ Postcode <span className="font-semibold">{postcodeConfirmed}</span> confirmed.
              </p>
              <div className="mt-2 flex gap-2">
                <button
                  type="button"
                  onClick={fetchAddresses}
                  className="btn-sm btn-secondary text-xs"
                  aria-label={`Find addresses for ${postcodeConfirmed}`}
                >
                  Find addresses
                </button>
                <span className="text-xs text-slate-500 self-center">
                  or enter the full address above
                </span>
              </div>
            </div>
          )}

          {/* Address suggestions dropdown */}
          {addressOptions.length > 0 && (
            <div className="mt-2 space-y-2" role="region" aria-label="Address suggestions">
              <p className="text-xs font-semibold text-slate-950">
                {addressOptions.length} address{addressOptions.length > 1 ? 'es' : ''} found
              </p>
              <div className="space-y-1 max-h-60 overflow-y-auto">
                {addressOptions.map((address, index) => (
                  <button
                    key={index}
                    type="button"
                    onClick={() => handleSelectAddress(address)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleSelectAddress(address);
                      }
                    }}
                    className="w-full text-left panel p-3 hover:bg-slate-50 focus:bg-slate-50 transition-colors text-sm"
                    aria-label={`Select address: ${address.line1}, ${address.city}, ${address.postcode}`}
                  >
                    <div className="font-medium text-slate-950">{address.line1}</div>
                    {address.line2 && <div className="text-slate-700">{address.line2}</div>}
                    <div className="text-slate-700">{address.city}, {address.postcode}</div>
                  </button>
                ))}
                <button
                  type="button"
                  onClick={() => {
                    setAddressOptions([]);
                  }}
                  className="w-full text-left panel p-3 hover:bg-slate-50 focus:bg-slate-50 transition-colors text-sm text-slate-600 italic"
                  aria-label="Enter address manually"
                >
                  Enter address manually
                </button>
              </div>
            </div>
          )}
        </div>

        <button
          type="submit"
          className="btn-primary w-full sm:w-auto px-5 py-3 text-base"
          disabled={loading}
          aria-busy={loading}
        >
          {loading ? 'Loading...' : buttonText}
        </button>
      </div>
    </form>
  );
}
